import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useMisAportes, useMisEmpresas } from '../hooks/useMisDatos';
import { useMisNovedades } from '../hooks/useNovedades';
import { useMisTramites } from '../hooks/useTramites';
import { EstadoPagoChip, EstadoTramiteChip } from '../components/Chips';
import { formatCurrency, formatDate, formatPeriodo } from '../utils/format';

function Dato({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card sx={{ flex: '1 1 200px', minWidth: 0 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export default function Inicio() {
  const { ficha } = useAuth();
  const { data: empresas } = useMisEmpresas();
  const { data: aportes } = useMisAportes();
  const { data: novedades } = useMisNovedades();
  const { data: tramites } = useMisTramites();

  const empresaActual = (empresas ?? []).find((e) => e.estado === 'activa');
  const ultimoAporte = (aportes ?? [])[0];
  const enCurso = (tramites ?? []).filter((t) => t.estado !== 'resuelto' && t.estado !== 'rechazado');
  const conPedido = (tramites ?? []).filter((t) => t.estado === 'falta_informacion');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Hola, {ficha?.nombre}
        </Typography>
        <Typography color="text.secondary">Afiliado/a desde {formatDate(ficha?.fecha_alta_sistema)}</Typography>
      </Box>

      {conPedido.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" component={RouterLink} to={`/tramites/${conPedido[0].id}`}>
              Responder
            </Button>
          }
        >
          El gremio te pidió información en {conPedido.length === 1 ? 'un trámite' : `${conPedido.length} trámites`}.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Dato label="Estado de afiliación">
          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
            {ficha?.estado}
          </Typography>
        </Dato>
        <Dato label="Empresa actual">
          <Typography variant="h6">{empresaActual?.razon_social ?? 'Sin empresa activa'}</Typography>
        </Dato>
        <Dato label={ultimoAporte ? `Último aporte (${formatPeriodo(ultimoAporte.periodo)})` : 'Último aporte'}>
          {ultimoAporte ? (
            <>
              <Typography variant="h6">{formatCurrency(ultimoAporte.aporte)}</Typography>
              <EstadoPagoChip estado={ultimoAporte.estado_pago} />
            </>
          ) : (
            <Typography variant="h6">Sin aportes</Typography>
          )}
        </Dato>
        <Dato label="Trámites en curso">
          <Typography variant="h6">{enCurso.length}</Typography>
        </Dato>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Novedades</Typography>
              <Button size="small" component={RouterLink} to="/novedades">
                Ver todas
              </Button>
            </Stack>
            {(novedades ?? []).length === 0 && <Typography color="text.secondary">No hay novedades por ahora.</Typography>}
            <Stack spacing={1.5}>
              {(novedades ?? []).slice(0, 3).map((n) => (
                <Box key={n.id}>
                  <Typography component="div" fontWeight={600}>
                    {n.titulo} {!n.leida && <Chip size="small" color="warning" label="Nueva" sx={{ ml: 0.5 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(n.publicada_at)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Mis trámites</Typography>
              <Button size="small" component={RouterLink} to="/tramites">
                Ver todos
              </Button>
            </Stack>
            {(tramites ?? []).length === 0 && <Typography color="text.secondary">Todavía no iniciaste ningún trámite.</Typography>}
            <Stack spacing={1.5}>
              {(tramites ?? []).slice(0, 3).map((t) => (
                <Stack key={t.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>
                      {t.tipo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.codigo} · {formatDate(t.created_at)}
                    </Typography>
                  </Box>
                  <EstadoTramiteChip estado={t.estado} />
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
